<template>
  <div class="request-review-form iu-card mt-4">
    <ListInvoiceProducts
      :invoice="invoice"
      :show-select="!isInvoicePending"
      :is-products-invalid="isProductsInvalid"
      @select="products = $event"
      class="mb-4"
    />

    <v-card
      class="pa-4"
      background-color="#f5f5f5"
      flat
    >
      <template v-if="!isInvoicePending">
        <p class="rrf-title">
          Você não concorda com:
        </p>

        <div class="rrf-not-agree-with">
          <v-checkbox
            v-model="form.notAgreeWith.dmProduct"
            :value="true"
            :error="isNotAgreeInvalid"
            color="secondary"
            label="Produto da Campanha"
          />
          <v-checkbox
            v-model="form.notAgreeWith.quantity"
            :value="true"
            :error="isNotAgreeInvalid"
            color="secondary"
            label="Quantidade calculada"
          />
          <v-checkbox
            v-model="form.notAgreeWith.status"
            :value="true"
            :error="isNotAgreeInvalid"
            color="secondary"
            label="Status"
          />
        </div>
      </template>

      <p
        v-if="isInvoicePending"
        class="rrf-title mb-1"
      >
        Observações
      </p>

      <AppTextArea
        v-model="form.description"
        @focusout="$v.form.description.$touch()"
        :rows="4"
        :v="$v.form.description"
        label="Descreva o que não está de acordo: "
        placeholder="Descreva o que não está de acordo:"
        min-length="50"
        max-length="5000"
        hide-details
      />
      <span class="text-muted">{{ form.description.length || '0' }}/5000</span>
      <br>
      <span class="text-muted">*Mínimo 50 caracteres.</span>


      <div class="upload-section">
        <input
          ref="uploadInput"
          @change="getRequestReviewFiles"
          accept=".PDF,.JPG,.JPEG,.PNG"
          type="file"
          hidden
          multiple
        >
        <v-btn
          @click="$refs.uploadInput.click()"
          outline
          color="secondary"
          flat
        >
          <i class="material-icons mr-2">cloud_upload</i> Importar arquivos
        </v-btn>
        <small class="gray--text mb-2">Somente arquivos .PDF, .JPG, .JPEG ou .PNG</small>
        <div
          v-if="files.length"
          class="selected-files"
        >
          {{ files.map(file => file.name).join(', ') }}
        </div>
      </div>

      <div class="submit-box">
        <v-btn
          @click="cancelRequestReview"
          outline
          color="secondary"
          flat
        >
          Voltar
        </v-btn>

        <v-btn
          @click="confirmRequestReview"
          color="primary"
        >
          Solicitar revisão
        </v-btn>
      </div>
    </v-card>
  </div>
</template>

<script>
  import AppTextArea from '@/components/ui/inputs/AppTextArea.vue'
  import ListInvoiceProducts from './ListInvoiceProducts.vue'
  import {
    requestInvoiceReview,
    requestInvoiceReviewUpload
  } from '@/services/containers/partners/invoices'
  import {
    required,
    minLength,
    maxLength,
    requiredIf
  } from 'vuelidate/lib/validators'

  const initialForm = () => {
    return {
      notAgreeWith: {
        status: false,
        dmProduct: false,
        quantity: false
      },
      description: '',
    }
  }

  export default {
    name: 'RequestReviewForm',

    components: {
      AppTextArea,
      ListInvoiceProducts
    },

    props: {
      invoice: {
        type: Object,
        required: true
      }
    },

    data() {
      return {
        files: [],
        form: initialForm(),
        products: []
      }
    },

    computed: {
      isNotAgreeInvalid() {
        return this.$v.$dirty && this.$v.form.notAgreeWith.$invalid
      },
      isProductsInvalid() {
        return this.$v.$dirty && this.$v.products.$invalid
      },
      isInvoicePending() {
        return this.invoice?.status === 'pending'
      },
    },

    methods: {
      async confirmRequestReview() {
        this.$v.$touch()
        if (this.$v.$invalid) {
          return
        }
        const swalInfo = {
          type: 'warning',
          title: 'Atenção',
          text: `Tem certeza que deseja solicitar revisão da NFe: ${this.invoice.invoiceNumber}?`,
          confirmButtonText: 'Solicitar revisão',
          cancelButtonText: 'Cancelar',
          showCancelButton: true,
          reverseButtons: true,
        }

        const { value } = await this.$swal(swalInfo)
        if (value) {
          await this.requestReview()
          this.$emit('requestReview')
        }
      },
      async requestReview() {
        try {

          let payload = {
            ...this.form,
            products: this.products.map(product => product._id)
          }

          if (this.isInvoicePending) {
            payload = {
              description: this.form.description
            }
          }

          const res = await requestInvoiceReview(this.invoice._id, payload)
          await this.requestReviewUpload(res.invoiceReview)
          this.$toast('Solicitação de revisão realizada com sucesso', 'green')
          this.$emit('finishReview')

        } catch (error) {
          if (error === 'upload-file-error') {
            this.$toast('A solicitação de revisão foi realizada com sucesso, porém não foi possível anexar arquivos.', 'red')
            return
          }
          const msg = error?.response?.data?.message || 'Não foi possível solicitar a revisão.'
          this.$toast(msg, 'red')
        }
      },

      getRequestReviewFiles(event) {
        const files = event.dataTransfer?.files ? event.dataTransfer.files : event.target.files
        this.files = Array.from(files)
      },

      async requestReviewUpload(reviewId) {
        try {
          if (!this.files.length) {
            return
          }
          await requestInvoiceReviewUpload(reviewId, this.files)
        } catch (error) {
          throw 'upload-file-error'
        }
      },

      cancelRequestReview() {
        this.files = []
        this.$emit('cancel')
        this.form = initialForm()
        this.$v.$reset()
      },
      close() {
        this.form = initialForm()
        this.$v.$reset()
      }
    },
    validations() {
      return {
        products: {
          required: requiredIf(!this.isInvoicePending),
          greaterThanZero: (value) => this.isInvoicePending || value.length > 0
        },
        form: {
          description: {
            required,
            minLength: minLength(50),
            maxLength: maxLength(5000)
          },
          notAgreeWith: {
            greaterThanZero: (value) => this.isInvoicePending || (value.dmProduct || value.status || value.quantity)
          }
        }
      }
    }
  }
</script>

<style lang="stylus" scoped>
.request-review-form {
  .rrf-title {
    color: var(--secondary-dark);
    font-weight: 600;
    text-align: left;
  }
  .rrf-not-agree-with {
    display: flex;
    flex-direction: column;

    @media (min-width: 769px) {
      flex-direction: row;
    }
  }
  .submit-box {
    display: flex;
    margin-top: 20px;
    justify-content: center;
    align-items: center;
  }

  .upload-section {
    display: flex;
    flex-direction: column;
    margin-top: 16px;
    align-items: center;

    .selected-files {
      margin-top: 16px;
      color: $base-light-color;
    }

    .btn:hover img{
      filter: brightness(0) invert(1);
      transition: all 500ms ease;
    }
  }  
}
</style>

<style lang="stylus">
.btn-cancel {
  border: 1px solid var(--v-primary-base) !important;
  box-shadow: none !important;
  background-color: white !important;
  color: var(--v-primary-base) !important;
}
</style>